import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, BellRing, Check, CheckCheck } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api, type ApprovalItem, type MarketplaceOut } from "@/lib/api";
import { formatPrice } from "@/lib/format";

const DEFAULT_MARKETPLACES: MarketplaceOut[] = [
  {
    id: "maisons_du_monde",
    group: "maisons_du_monde",
    label: "Maisons du Monde IT",
    country: "IT",
    kind: "mirakl",
    enabled: true,
  },
];

// Il backend invia delta_pct come FRAZIONE (es. "0.61" = 61%): qui la
// convertiamo in percentuale per il display.
function formatDeltaPct(value: string | null): string {
  if (value === null) return "-";
  const n = Number(value);
  if (Number.isNaN(n)) return "-";
  return `${(n * 100).toFixed(1)}%`;
}

export function Approvals(): JSX.Element {
  const qc = useQueryClient();
  const marketplaces = useQuery({ queryKey: ["marketplaces"], queryFn: api.marketplaces });
  // Solo marketplace scrivibili: su un kind read-only (Amazon) approvare non ha senso.
  const options = useMemo(
    () => (marketplaces.data ?? DEFAULT_MARKETPLACES).filter((m) => m.kind !== "amazon"),
    [marketplaces.data],
  );
  // Link dalla Panoramica: `?canale=` sceglie il canale da approvare.
  const [params] = useSearchParams();
  const canaleUrl = params.get("canale");
  const [marketplaceId, setMarketplaceId] = useState(canaleUrl ?? "maisons_du_monde");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (options.length === 0) return;
    if (canaleUrl && !marketplaces.data) return;
    if (!options.some((m) => m.id === marketplaceId)) {
      setMarketplaceId(options[0].id);
    }
  }, [options, marketplaceId, canaleUrl, marketplaces.data]);

  const approvals = useQuery({
    queryKey: ["approvals", marketplaceId],
    queryFn: () => api.approvals(marketplaceId),
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Il backend risponde 200 anche quando la scrittura fallisce (applied=false
  // con l'errore): lo trasformiamo in errore, se no la pagina taceva e il
  // prezzo restava quello vecchio senza che nessuno lo sapesse.
  const approve = useMutation({
    mutationFn: async (sku: string) => {
      const res = await api.approveReprice(sku, marketplaceId);
      if (!res.applied) throw new Error(`${res.sku}: ${res.error || "scrittura non riuscita"}`);
      return res;
    },
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
  });
  const approveBulk = useMutation({
    mutationFn: (skus: string[]) =>
      api.approveBulk(skus.map((sku) => ({ sku, marketplace_id: marketplaceId }))),
    onSuccess: (data) => {
      // I falliti restano selezionati: si possono ritentare subito.
      setSelected(new Set(data.failed.map((f) => f.sku)));
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
  });

  function changeMarketplace(id: string): void {
    // Gli stessi sku esistono su piu' canali (Leroy IT/FR/ES): una selezione
    // fatta su un canale non deve diventare un'approvazione sull'altro.
    setSelected(new Set());
    approve.reset();
    approveBulk.reset();
    setMarketplaceId(id);
  }

  const rows = approvals.data ?? [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.sku));
  const someSelected = selected.size > 0 && !allSelected;
  // La checkbox header non ha stato "indeterminate" via JSX: lo settiamo via ref
  // quando la selezione e' parziale (accessibilita' multi-selezione).
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  function toggle(sku: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  }
  function toggleAll(): void {
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.sku)));
  }
  function approveSelected(): void {
    const skus = rows.filter((r) => selected.has(r.sku)).map((r) => r.sku);
    if (skus.length === 0) return;
    // Conferma: l'approvazione in blocco scrive prezzi REALI sul marketplace.
    if (!window.confirm(`Approvare ${skus.length} prezzi su ${marketplaceId}? Scrive sul marketplace.`)) {
      return;
    }
    approveBulk.mutate(skus);
  }

  const busy = approve.isPending || approveBulk.isPending;

  return (
    <section className="space-y-6 px-7 pb-8 pt-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl space-y-1">
          <h1 className="page-title">Da approvare</h1>
          <p className="page-subtitle">
            Suggerimenti oltre la soglia auto-apply: richiedono la tua approvazione.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="control-shell min-h-10 gap-2 px-3 text-xs font-medium text-muted-foreground">
            Marketplace
            <select
              value={marketplaceId}
              onChange={(event) => changeMarketplace(event.target.value)}
              className="control-select max-w-52 bg-transparent text-sm text-foreground outline-none"
            >
              {options.map((marketplace) => (
                <option key={marketplace.id} value={marketplace.id}>
                  {marketplace.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={approveSelected}
            disabled={busy || selected.size === 0}
            className="btn btn-accent"
          >
            <CheckCheck className="h-4 w-4" strokeWidth={1.5} />
            Approva selezionati ({selected.size})
          </button>
        </div>
      </header>

      {approvals.isError && (
        <div className="rounded-[var(--radius)] border border-destructive/35 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Errore caricamento alert: {String(approvals.error ?? "errore sconosciuto")}
        </div>
      )}

      {(approve.isError || approveBulk.isError) && (
        <div className="rounded-[var(--radius)] border border-destructive/35 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Errore approvazione (prezzo NON scritto):{" "}
          {String(approve.error ?? approveBulk.error ?? "errore sconosciuto")}
        </div>
      )}

      {approveBulk.data && (
        <div className="rounded-[var(--radius)] border border-line bg-card px-4 py-3 text-md">
          Applicati {approveBulk.data.applied} / {approveBulk.data.results.length}.
          {approveBulk.data.failed.length > 0 && (
            <>
              <span className="text-destructive">
                {" "}
                {approveBulk.data.failed.length} falliti (restano selezionati):
              </span>
              <ul className="mt-1 list-disc pl-5 text-sm text-destructive">
                {approveBulk.data.failed.map((f) => (
                  <li key={f.sku}>
                    <span className="font-mono">{f.sku}</span>: {f.error || "scrittura non riuscita"}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {rows.length > 0 ? (
        <section className="panel overflow-x-auto">
          <div className="min-w-[1080px]">
            <div className="grid grid-cols-[40px_minmax(200px,1fr)_120px_130px_110px_90px_140px_120px] items-center gap-x-4 border-b border-line bg-paper-2 px-4 py-2.5 text-label font-semibold text-ink-3">
              <div>
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Seleziona tutti"
                />
              </div>
              <div>SKU</div>
              <div className="text-right">Attuale</div>
              <div className="text-right">Suggerito</div>
              <div className="text-right">Floor</div>
              <div className="text-right">Taglio</div>
              <div className="text-right">Valore in gioco</div>
              <div className="text-right">Azione</div>
            </div>
            <ul>
              {rows.map((item) => (
                <ApprovalRow
                  key={`${item.marketplace_id}-${item.sku}`}
                  item={item}
                  checked={selected.has(item.sku)}
                  onToggle={() => toggle(item.sku)}
                  onApprove={() => approve.mutate(item.sku)}
                  disabled={busy}
                />
              ))}
            </ul>
          </div>
        </section>
      ) : approvals.isLoading ? (
        <div className="panel p-4">
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, index) => (
              <div key={`skeleton-${index}`} className="h-12 animate-pulse rounded-md bg-paper-2" />
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-state min-h-56 gap-4 p-12 text-center">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-line-2 bg-paper">
            <BellRing className="h-5 w-5 text-ink-3" strokeWidth={1.6} aria-hidden="true" />
          </span>
          <div>
            <p className="text-lg font-semibold tracking-tight text-foreground">Nessun alert da approvare</p>
            <p className="mt-1 text-sm text-muted-foreground">
              I suggerimenti oltre soglia appariranno qui dopo il prossimo refresh.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

interface ApprovalRowProps {
  item: ApprovalItem;
  checked: boolean;
  onToggle: () => void;
  onApprove: () => void;
  disabled: boolean;
}

function ApprovalRow({ item, checked, onToggle, onApprove, disabled }: ApprovalRowProps): JSX.Element {
  return (
    <li className="grid grid-cols-[40px_minmax(200px,1fr)_120px_130px_110px_90px_140px_120px] items-center gap-x-4 border-b border-line px-4 py-2.5 text-md transition-colors last:border-b-0 hover:bg-paper-2">
      <div>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label={`Seleziona ${item.sku}`}
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="font-code font-medium">{item.sku}</span>
        {item.critical && (
          <span className="pill pill-out">
            <AlertTriangle className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
            Critico
          </span>
        )}
      </div>
      <div className="text-right font-mono tabular-nums text-muted-foreground">
        {formatPrice(item.current_price)}
      </div>
      <div className="flex items-center justify-end gap-1.5 font-mono font-semibold tabular-nums">
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
        {formatPrice(item.suggested_price)}
      </div>
      <div className="text-right font-mono tabular-nums text-muted-foreground">
        {formatPrice(item.floor_price)}
      </div>
      <div className="text-right font-mono tabular-nums">{formatDeltaPct(item.delta_pct)}</div>
      <div className="text-right font-mono tabular-nums">
        {formatPrice(item.value_at_stake_eur)}
      </div>
      <div className="text-right">
        <button
          type="button"
          onClick={onApprove}
          disabled={disabled}
          className="btn btn-sm btn-outline"
          aria-label={`Approva ${item.sku}`}
        >
          <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
          Approva
        </button>
      </div>
    </li>
  );
}
